// src/tattoo/projectsIndex.js
const RECENT_KEY = "tattoo:recent-projects:v1";
export const SNAPSHOT_KEY_PREFIX = "tt_project_snapshot:";
const MAX_RECENTS = 3;

// projectsIndex.js
import { idbPut, idbGet } from "./idbAutosave"; // same IDB helpers you already use

const RECENTS_KEY = "tattoo:recent-projects:v1";
const RECENT_THUMB_IDB_KEY = (projectId) => `__recent_thumb__:${projectId}`;

const RECENT_THUMB_KEY = (id) => `tattoo:recent-thumb:v1:${id}`;

function safeParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}



export function getRecentProjects() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const list = raw ? safeParse(raw, []) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

// NEW: async helper to hydrate thumbs when Dashboard renders
export async function getRecentThumb(projectId) {
  if (!projectId) return null;
  try {
    const rec = await idbGet(RECENT_THUMB_IDB_KEY(projectId));
    return rec?.thumbDataUrl || null;
  } catch {
    return null;
  }
}

export function upsertRecentProject({
  id,
  projectName,
  sex,
  thumb,
  viewSnap,
}) {
  if (!id) return;

  // 1) Write thumbnail to IDB (async, fire-and-forget)
  try {
    if (
      thumb &&
      typeof thumb === "string" &&
      thumb.startsWith("data:image/")
    ) {
      idbPut(RECENT_THUMB_IDB_KEY(id), {
        projectId: id,
        thumbDataUrl: thumb,
        updatedAt: Date.now(),
      }).catch(() => {});
    }
  } catch {}

  // 2) Update metadata in localStorage
  let list = getRecentProjects();

  const existing = list.find((x) => x?.id === id);
  const pinned = !!existing?.pinned;

  // 🐛 Diagnostic: log EVERY call with a stack trace. During the phantom
  // hunt we need to see all callers, not just empty-name ones — the
  // recents-write path is somehow still adding phantoms and we don't
  // know from where. Remove after phantoms confirmed gone.
  console.warn("[recents] UPSERT called", { id, projectName, sex });
  console.trace("[recents] caller");

  const entry = {
    id,
    projectName: projectName || "Tattune",
    sex: sex || "male",
    pinned,
    updatedAt: Date.now(),
    viewSnap: viewSnap || null, // ✅ camera snapshot
  };

  list = list.filter((x) => x?.id !== id);
  list.unshift(entry);
  list = list.slice(0, 12);

  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch {}
}

export function cleanupOldSnapshots(maxToKeep = MAX_RECENTS) {
  try {
    const recents = getRecentProjects();
    const sorted = recents
      .slice()
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

    const keep = sorted.slice(0, maxToKeep);
    const keepIds = new Set(keep.map((p) => p.id));

    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!key.startsWith(SNAPSHOT_KEY_PREFIX)) continue;

      const projectId = key.substring(SNAPSHOT_KEY_PREFIX.length);

      // always drop local:* snapshots (your original behavior preserved)
      if (projectId.startsWith("local:")) {
        keysToRemove.push(key);
        continue;
      }

      if (!keepIds.has(projectId)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      console.log("[storage] cleanup: removing old snapshot", key);
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn("[storage] failed to remove snapshot", key, e);
      }
    });

    // trim recents if longer than max
    if (recents.length > maxToKeep) {
      const trimmed = sorted.slice(0, maxToKeep);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(trimmed));
      } catch (e) {
        console.warn("[storage] failed to trim recents:", e);
      }
    }
  } catch (e) {
    console.warn("[storage] cleanupOldSnapshots failed:", e);
  }
}

export async function removeRecentProject(projectId) {
  if (!projectId) return;

  // 1) Remove from recents list
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = list.filter((p) => p?.id !== projectId);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {}

  // 2) Remove localStorage snapshot (small projects)
  try {
    localStorage.removeItem(`tt_project_snapshot:${projectId}`);
  } catch {}

  // 3) Remove IDB snapshot (big projects)
  try {
    await idbDel(`__snapshot__:${projectId}`);
  } catch {}

  // 4) Remove thumbnail from IDB
  try {
    await idbDel(RECENT_THUMB_IDB_KEY(projectId));
  } catch {}

  console.log("[recent] removed project", projectId);
}

export function togglePinRecentProject(projectId) {
  if (!projectId) return;

  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const list = raw ? JSON.parse(raw) : [];

    const next = list.map((p) => {
      if (p?.id !== projectId) return p;
      return { ...p, pinned: !p.pinned };
    });

    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {}
}

export async function clearAllRecents() {
  // wipe recents list
  try {
    localStorage.removeItem(RECENTS_KEY);
  } catch {}

  // optional: wipe all localStorage snapshots (small projects)
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("tt_project_snapshot:")) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {}

  console.log("[recent] cleared all recents");
}